import React, { forwardRef, useEffect, useState } from 'react';
import InputMask from 'react-input-mask';
import validator from 'validator';
import { CalendarIconDark, Download } from '../../assets/icons/svgIconsForJSX';
import { classNameBuilder } from '../../utils/className';
import DatePickerCalendar from '../DatePickerCalendar/DatePickerCalendar';
import SelectField from '../SelectField/SelectField';
import './InputField.scss';
import { EMAIL_MAX_LENGTH, PASSWORD_MAX_LENGTH, isValidEmailSymbols } from '../../utils/validation';

interface IProps {
    onChange: (e: any) => void;
    variant?: 'INPUT' | 'TEXTAREA' | 'FILE' | 'DATE' | 'SELECT' | 'PHONE';
    placeholder?: string;
    className?: any;
    type?: string;
    name: string;
    title?: string;
    theme?: 'light' | 'dark';
    required?: boolean;
    isValid?: boolean;
    value?: any;
    isValidationLaunchedByClick?: boolean;
    helper?: string;
    option?: any;
    isValidDate?: boolean;
    isDisabled?: boolean;
    isValidFocus?: boolean;
    mismatch?: boolean;
    accept?: string;
    autoComplete?: string;
    tooltip?: string;
    fullWidth?: boolean;
    isValidSymbols?: (v: string) => boolean;
    maxLength?: number;
}

const allSymbolsAreValid = () => true;

const InputField: React.FC<IProps> = ({
    title,
    type = 'text',
    name,
    className,
    onChange,
    variant = 'INPUT',
    placeholder = 'Введите текст',
    required = false,
    value,
    isValid,
    isValidDate,
    isValidationLaunchedByClick,
    theme = 'dark',
    helper,
    option,
    isDisabled = false,
    isValidFocus = false,
    mismatch = false,
    accept,
    autoComplete = 'off',
    tooltip = '',
    fullWidth,
    isValidSymbols = allSymbolsAreValid,
    maxLength,
}) => {
    if ('email' === type && isValidSymbols === allSymbolsAreValid) {
        isValidSymbols = isValidEmailSymbols;
        maxLength = EMAIL_MAX_LENGTH;
    }
    if ('password' === type) {
        maxLength = PASSWORD_MAX_LENGTH;
    }

    const cn = classNameBuilder('input-field');
    const [check, setChek] = useState(isValid);

    const [isTouched, setTouched] = useState(false);
    const [isFocused, setFocused] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const [dateValue, setDateValue] = useState('');

    useEffect(() => {
        if (
            validator.isDate(dateValue, {
                format: 'DD-MM-YYYY',
            })
        ) {
            const [day, month, year] = dateValue.split('-');
            onChange(`${year}-${month}-${day}`);
        }
    }, [dateValue]);

    useEffect(() => {
        setTouched(false);
    }, [name]);
    useEffect(() => {
        setChek(isValid);
    }, [isValid]);

    useEffect(() => {
        if (
            validator.isDate(value, {
                format: 'YYYY-MM-DD',
            })
        ) {
            setDateValue(() => {
                if (value === undefined) return '';
                if (value instanceof Date) {
                    const year = new Intl.DateTimeFormat('en', { year: 'numeric' }).format(value);
                    const month = new Intl.DateTimeFormat('en', { month: '2-digit' }).format(value);
                    const day = new Intl.DateTimeFormat('en', { day: '2-digit' }).format(value);
                    return `${day}-${month}-${year}`;
                }
                const [year, month, day] = value.split('-');
                return `${day}-${month}-${year}`;
            });
        }
    }, [value]);

    const isInvalid = () => {
        if (required && isValidFocus) {
            return isTouched && isValid !== undefined && !isValid;
        } else if (isValidationLaunchedByClick || isValidDate) {
            return !isFocused && !isValid;
        } else {
            return (
                (required || !!value) &&
                !isFocused &&
                isTouched &&
                isValid !== undefined &&
                !isValid
            );
        }
    };

    const CustomDateInput = forwardRef(({ value, onClick, onChange }: any, ref: any) => {
        const [localValue, setLocalValue] = useState(dateValue || '');
        useEffect(() => {
            const normalizeValue = localValue.replace(/ /g, '');

            if (normalizeValue.length >= 10) {
                setTouched(true);
            }
            if (
                validator.isDate(normalizeValue, {
                    format: 'DD-MM-YYYY',
                }) ||
                normalizeValue.length >= 10
            ) {
                setDateValue(localValue);
            }
        }, [localValue]);

        const changeDateHandler = (date: string) => {
            setLocalValue(date);
        };

        const invalid =
            isInvalid() ||
            (!validator.isDate(localValue, {
                format: 'DD-MM-YYYY',
            }) &&
                (isTouched || localValue.replace(/ /g, '').length >= 10));

        return (
            <div className={cn('input-box')} ref={ref}>
                <div
                    className={`${cn('date-input', { theme: theme })} 
                     ${(isValid && cn('date-input--valid', { theme: theme })) || ''}
                     ${(invalid && cn('date-input--invalid', { theme: theme })) || ''} 
                     ${className || ''}`}>
                    <div className={cn('show--data')}>
                        <InputMask
                            maskChar=" "
                            mask={'99-99-9999'}
                            onChange={(e) => {
                                changeDateHandler(e.target.value);
                            }}
                            className={cn('show--data-input')}
                            placeholder={'__ - __ - ____'}
                            value={localValue}
                        />
                        <button onClick={onClick} ref={ref}>
                            <CalendarIconDark />
                        </button>
                    </div>
                </div>
            </div>
        );
    });

    return (
        <label className={cn('', { fullWidth })}>
            {title && (
                <p className={cn('label_text', { theme: theme })}>
                    {required && <span className={cn('required')}>*</span>} {title}
                </p>
            )}
            <div className={cn('input-box')}>
                {variant === 'INPUT' && (
                    <>
                        <input
                            maxLength={maxLength}
                            autoComplete={autoComplete}
                            required={required}
                            value={value}
                            type={type === 'password' ? (showPassword ? 'text' : 'password') : type}
                            className={
                                //     type === 'password'
                                //         ? `${cn('text-input', { theme: 'password' })}
                                // ${(isValid && cn('text-input--valid', { theme: 'password' })) || ''}
                                // ${
                                //     (isInvalid() && cn('text-input--invalid', { theme: 'password' })) ||
                                //     ''
                                // }
                                // ${className || ''}`
                                //         :
                                `${cn('text-input', { theme: theme })} 
                            ${(check && cn('text-input--valid', { theme: theme })) || ''}
                            ${(isInvalid() && cn('text-input--invalid', { theme: theme })) || ''} 
                            ${className || ''}`
                            }
                            name={name}
                            onChange={(e) => {
                                if (isValidSymbols(e.target.value)) {
                                    onChange(e);
                                }
                                isValidFocus && setTouched(isValidFocus);
                            }}
                            placeholder={placeholder}
                            onFocus={(e) => {
                                e.target.setAttribute('autocomplete', 'new-password');
                                setFocused(true);
                            }}
                            onBlur={(e) => {
                                setFocused(false);
                                setTouched(true);
                            }}
                            onKeyDown={(e) => {
                                if (!isValidSymbols(e.key)) {
                                    e.preventDefault();
                                }
                            }}
                        />
                        {isFocused && !isInvalid() && tooltip && (
                            <div className={cn('helper-password')}>{tooltip}</div>
                        )}
                        {type === 'password' && (
                            <div
                                className={cn('show-password-btn')}
                                onClick={() => setShowPassword(!showPassword)}
                            />
                        )}
                        {name === 'passwordFirst' && isFocused && helper && (
                            <div className={cn('helper-password')}>{helper}</div>
                        )}
                        {helper && name === 'email' && isInvalid() && (
                            <div className={cn('helper-email')}>{helper}</div>
                        )}
                        {name === 'passwordConfirmation' && mismatch && !isFocused && (
                            <div className={cn('helper-mismatch-password')}>{helper}</div>
                        )}
                    </>
                )}
                {variant === 'DATE' && (
                    <DatePickerCalendar
                        onChange={onChange}
                        value={value}
                        customInput={<CustomDateInput />}
                        theme={'dark'}
                        maxDate={new Date()}
                    />
                )}
                {variant === 'SELECT' && (
                    <SelectField
                        components={{
                            IndicatorSeparator: () => null,
                        }}
                        className={cn('select')}
                        name={name}
                        options={option}
                        onChange={onChange}
                        value={value}
                        isDisabled={isDisabled}
                        placeholder={placeholder}
                        styles={{
                            control: (base: any, state: { isFocused: any }) => ({
                                ...base,
                                borderColor:
                                    (!isDisabled &&
                                        !!option[0] &&
                                        isInvalid() &&
                                        '#ff5436!important') ||
                                    (isValid && '#0067BB!important'),
                                colors: {
                                    primary25: 'hotpink',
                                    primary: 'black',
                                },
                                background: '#112652',
                                border: '1.5px solid #091C45',
                                borderRadius: '8px',
                                width: '344px',
                                color: '#ffffff',
                                height: '51px',
                                boxShadow: 'none',
                                '&:hover': {
                                    border: '2px solid #78e6d0',
                                },
                            }),
                            option: (styles: any, { isDisabled, isFocused, isSelected }: any) => ({
                                ...styles,
                                // backgroundColor: '#112651',
                                color: '#091c44',
                                // '&:hover': {
                                //     backgroundColor: '#0067bb',
                                // },
                            }),
                            singleValue: (styles: any) => ({
                                ...styles,
                                color: '#ffffff',
                                marginLeft: '12px',
                                fontSize: '16px',
                                fontWeight: '500',
                            }),
                            input: (styles: any) => ({
                                ...styles,
                                color: '#ffffff',
                                marginLeft: '12px',
                                fontSize: '16px',
                                fontWeight: '500',
                            }),
                        }}
                    />
                )}
                {variant === 'TEXTAREA' && (
                    <textarea
                        maxLength={maxLength}
                        required={required}
                        rows={5}
                        cols={5}
                        className={`${cn('textarea-input')} 
              ${(isValid && cn('textarea-input--valid')) || ''}
              ${(isInvalid() && cn('textarea-input--invalid')) || ''}
              ${className || ''}`}
                        name={name}
                        onChange={(e) => {
                            if (isValidSymbols(e.target.value)) {
                                onChange(e);
                            }
                        }}
                        onKeyDown={(e) => {
                            if (!isValidSymbols(e.key)) {
                                e.preventDefault();
                            }
                        }}
                        placeholder={placeholder}
                        onFocus={(e) => {
                            // for disable autocomplete
                            e.target.setAttribute('autocomplete', 'new-password');
                            setFocused(true);
                        }}
                        onBlur={() => {
                            setFocused(false);
                            setTouched(true);
                        }}
                        value={value}
                    />
                )}
                {variant === 'FILE' && (
                    <div
                        className={`
              ${cn('file-input')}
              ${(isInvalid() && cn('file-input--invalid')) || ''}
              ${cn('input-box')}`}>
                        <input
                            type="file"
                            required={required}
                            name={name}
                            onChange={(e: any) => {
                                onChange(e);
                            }}
                            style={{ display: 'none' }}
                            accept={accept}
                        />
                        <p className={cn('placeholder')}>{value ? value.name : placeholder}</p>
                        <Download className={cn('file-download-icon')} />
                    </div>
                )}
            </div>
        </label>
    );
};

export default InputField;
